import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { PermissionKey, Scope } from '@shared/permissions';
import { useAuthState } from '@/features/auth/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from './paths';

/**
 * Route guards.
 *
 * These exist for USABILITY — they stop a user landing on a page that will only 403 at
 * them. They are not authorization: the backend re-checks every request, and a guard
 * that is bypassed costs nothing beyond a wasted render.
 */

function FullPageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-canvas">
      <span className="sr-only">Loading</span>
      <div
        aria-hidden
        className="size-6 animate-spin rounded-full border-2 border-line border-t-action"
      />
    </div>
  );
}

export function RequireAuth() {
  const { status } = useAuthState();
  const location = useLocation();

  // Hold while the session check is in flight. Redirecting here is what makes a page
  // refresh bounce an authenticated user to the login screen.
  if (status === 'unknown') return <FullPageSpinner />;

  if (status !== 'authenticated') {
    // The intended destination rides along, so signing in returns the user where they
    // were trying to go rather than dumping them on the dashboard.
    return <Navigate to={paths.login} replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

/**
 * Blocks the main application chrome until a forced password change is complete.
 * The change-password screen lives outside `AppShell` on its own layout.
 */
export function RequireAppAccess() {
  const { user } = useAuthState();

  if (user?.mustChangePassword) {
    return <Navigate to={paths.changePassword} replace />;
  }

  return <Outlet />;
}

export function GuestOnly() {
  const { status, user } = useAuthState();

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'authenticated') {
    if (user?.mustChangePassword) {
      return <Navigate to={paths.changePassword} replace />;
    }
    return <Navigate to={paths.dashboard} replace />;
  }

  return <Outlet />;
}

/**
 * Requires a permission to render a route.
 *
 * Renders the Forbidden page rather than an empty shell, so a user who follows a direct
 * link learns what is wrong instead of staring at chrome with nothing in it.
 */
export function RequirePermission({
  permission,
  scope,
}: {
  permission: PermissionKey;
  scope?: Scope;
}) {
  const { can } = usePermissions();
  if (!can(permission, scope)) return <Navigate to={paths.forbidden} replace />;
  return <Outlet />;
}

/** Renders the route when the user holds any one of the listed permissions. */
export function RequireAnyPermission({
  rules,
}: {
  rules: { permission: PermissionKey; scope?: Scope }[];
}) {
  const { can } = usePermissions();
  if (!rules.some((rule) => can(rule.permission, rule.scope))) {
    return <Navigate to={paths.forbidden} replace />;
  }
  return <Outlet />;
}
